import React, { useState, useCallback, useEffect, useRef, memo } from 'react';
import type { WorkflowNodeInfo, WorkflowTrace } from '../../types/contentBlock';

export interface WorkflowTracerProps {
  readonly workflow?: WorkflowTrace | null;
  readonly nodes: readonly WorkflowNodeInfo[];
  readonly isStreaming?: boolean;
}

/**
 * 节点类型 → Font Awesome 6.4 图标映射
 */
const NODE_TYPE_ICONS: Readonly<Record<string, string>> = {
  'start': 'fa-circle-play',
  'end': 'fa-circle-stop',
  'llm': 'fa-robot',
  'code': 'fa-code',
  'template': 'fa-file-lines',
  'if_else': 'fa-code-branch',
  'knowledge_retrieval': 'fa-database',
  'http_request': 'fa-globe',
  'tool': 'fa-screwdriver-wrench',
  'variable_assigner': 'fa-right-left',
  'question_classifier': 'fa-tags',
  'answer': 'fa-comment-dots',
  'agent': 'fa-robot'
};

const getNodeIcon = (nodeType: string): string =>
  NODE_TYPE_ICONS[nodeType] ?? 'fa-circle';

const getStatusClass = (status?: string): string => {
  switch (status) {
    case 'running': return 'wf-status--running';
    case 'succeeded': return 'wf-status--succeeded';
    case 'failed': return 'wf-status--failed';
    case 'stopped': return 'wf-status--stopped';
    default: return 'wf-status--pending';
  }
};

const getStatusIcon = (status?: string): string => {
  switch (status) {
    case 'running': return 'fa-spinner fa-spin';
    case 'succeeded': return 'fa-check';
    case 'failed': return 'fa-xmark';
    case 'stopped': return 'fa-pause';
    default: return 'fa-ellipsis';
  }
};

/**
 * 单个工作流节点
 */
const WorkflowNode: React.FC<{
  readonly node: WorkflowNodeInfo;
  readonly isLast: boolean;
}> = memo(({ node, isLast }) => {
  const [expanded, setExpanded] = useState(false);

  const handleToggle = useCallback((): void => {
    setExpanded(prev => !prev);
  }, []);

  const hasDetails = node.outputs || node.error;

  return (
    <div className={`wf-node ${getStatusClass(node.status)} ${isLast ? 'wf-node--last' : ''}`}>
      {/* 左侧：时间轴轨道 */}
      <div className="wf-node__rail">
        <span className={`wf-node__dot ${getStatusClass(node.status)}`}>
          <i className={`fa-solid ${getStatusIcon(node.status)}`} />
        </span>
        {!isLast && <span className="wf-node__line" />}
      </div>

      {/* 右侧：内容 */}
      <div className="wf-node__body">
        <button
          type="button"
          className="wf-node__header"
          onClick={handleToggle}
          disabled={!hasDetails}
        >
          <span className="wf-node__type-icon">
            <i className={`fa-solid ${getNodeIcon(node.nodeType)}`} />
          </span>
          <span className="wf-node__title">{node.title}</span>
          {node.elapsedTime != null && (
            <span className="wf-node__time">{node.elapsedTime.toFixed(1)}s</span>
          )}
          {hasDetails && (
            <span className={`wf-node__chevron ${expanded ? 'wf-node__chevron--open' : ''}`}>
              <i className="fa-solid fa-chevron-down" />
            </span>
          )}
        </button>

        {expanded && hasDetails && (
          <div className="wf-node__details">
            {node.error && (
              <div className="wf-node__error">
                <i className="fa-solid fa-triangle-exclamation" /> {node.error}
              </div>
            )}
            {node.outputs && (
              <pre className="wf-node__outputs">
                {JSON.stringify(node.outputs, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

WorkflowNode.displayName = 'WorkflowNode';

/**
 * 工作流追踪器
 *
 * - 默认展开
 * - 回答结束后（isStreaming false → true → false）自动折叠
 * - 类似截图中 "Steps taken by AI Agent" 样式
 */
export const WorkflowTracer: React.FC<WorkflowTracerProps> = memo(({
  workflow,
  nodes,
  isStreaming = false
}) => {
  // 非流式（已完成/历史消息）时默认折叠
  const [collapsed, setCollapsed] = useState(!isStreaming);
  const wasStreamingRef = useRef(isStreaming);

  // 自动收起：isStreaming 从 true → false 时，延迟折叠
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      const timer = setTimeout(() => {
        setCollapsed(true);
      }, 800);
      return () => clearTimeout(timer);
    }
    wasStreamingRef.current = isStreaming;
    return undefined;
  }, [isStreaming]);

  const handleToggle = useCallback((): void => {
    setCollapsed(prev => !prev);
  }, []);

  const workflowStatus = workflow?.status ?? 'running';
  const totalSteps = nodes.length;
  const completedSteps = nodes.filter(n => n.status === 'succeeded').length;

  const statusLabel = isStreaming
    ? '执行中'
    : workflowStatus === 'succeeded'
      ? '已完成'
      : workflowStatus === 'failed'
        ? '执行失败'
        : workflowStatus;

  return (
    <div className={`wf-tracer ${collapsed ? 'wf-tracer--collapsed' : 'wf-tracer--expanded'}`}>
      {/* 标题栏 */}
      <button
        type="button"
        className="wf-tracer__header"
        onClick={handleToggle}
        aria-expanded={!collapsed}
      >
        <span className={`wf-tracer__status-icon ${getStatusClass(workflowStatus)}`}>
          {isStreaming ? (
            <i className="fa-solid fa-spinner fa-spin" />
          ) : (
            <i className={`fa-solid ${getStatusIcon(workflowStatus)}`} />
          )}
        </span>

        <span className="wf-tracer__title">
          工作流执行 · {statusLabel}
        </span>

        <span className="wf-tracer__meta">
          {completedSteps}/{totalSteps} 步
        </span>

        {workflow?.elapsedTime != null && (
          <span className="wf-tracer__time">
            {workflow.elapsedTime.toFixed(1)}s
          </span>
        )}

        <span className={`wf-tracer__chevron ${collapsed ? 'wf-tracer__chevron--closed' : ''}`}>
          <i className="fa-solid fa-chevron-down" />
        </span>
      </button>

      {/* 节点列表 */}
      {!collapsed && (
        <div className="wf-tracer__body">
          {nodes.map((node, idx) => (
            <WorkflowNode
              key={`${node.nodeId}-${node.index}`}
              node={node}
              isLast={idx === nodes.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
});

WorkflowTracer.displayName = 'WorkflowTracer';